// WhatsApp handoff card — the "WHAT TO SAY" sales-playbook block.
//
// Rendered by AssistantBubble ONLY when (a) a sample's delivery message carries
// the `[[handoff:<url>]]` marker AND (b) the open chat is a SAMPLE (kind==='sample').
// The moment a sample is converted to a client, the gate in AssistantBubble drops
// this card — so a real client never sees "made your site, it's live".
//
// The two message texts are HARDCODED here (Julian's playbook), so the agent can
// never garble them — the agent only supplies the deployed URL. Each message has a
// one-tap Copy button → the salesperson pastes straight into WhatsApp; the live
// https:// link in message 1 makes WhatsApp render a link preview.

import { useRef, useState } from "react";

interface Props {
  /** The deployed sample URL (already validated http(s) by the caller's regex). */
  url: string;
}

/** The exact two messages, per Julian's sales playbook. Message 1 embeds the URL
 *  on its own line so WhatsApp generates a link preview. */
function messageOne(url: string): string {
  return `made your site, it's live 👉\n${url}`;
}
const MESSAGE_TWO = "what do you think? anything you want to change, i'll sort it out";

export default function WhatsappHandoffCard({ url }: Props) {
  return (
    <div className="handoff" role="group" aria-label="What to say — WhatsApp handoff">
      <div className="handoff__label">What to say</div>
      <div className="handoff__card">
        <div className="handoff__lead">
          <LinkGlyph />
          <span>Send both messages, one right after the other</span>
        </div>

        <HandoffMessage
          step="Message 1 · send the link"
          copyText={messageOne(url)}
        >
          made your site, it&apos;s live 👉
          <br />
          <a
            className="handoff__link"
            href={url}
            target="_blank"
            rel="noreferrer noopener"
            onClick={(e) => e.stopPropagation()}
          >
            {url}
          </a>
        </HandoffMessage>

        <HandoffMessage
          step="Message 2 · send right after"
          copyText={MESSAGE_TWO}
        >
          {MESSAGE_TWO}
        </HandoffMessage>
      </div>
    </div>
  );
}

/* One message row: label + a copy-box (header hint + Copy button + body preview). */
function HandoffMessage({
  step,
  copyText,
  children,
}: {
  step: string;
  copyText: string;
  children: React.ReactNode;
}) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | null>(null);

  function copy() {
    navigator.clipboard.writeText(copyText).catch(() => {});
    setCopied(true);
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setCopied(false), 1600);
  }

  return (
    <div className="handoff__msg">
      <div className="handoff__step">{step}</div>
      <div className={`handoff__box ${copied ? "is-copied" : ""}`}>
        <div className="handoff__boxHead">
          <span className="handoff__hint">Tap to copy → paste in WhatsApp</span>
          <button
            type="button"
            className="handoff__copy"
            onClick={copy}
            aria-label={copied ? "Copied" : "Copy this message"}
            title={copied ? "Copied" : "Copy"}
          >
            {copied ? <CheckGlyph /> : <CopyGlyph />}
            <span>{copied ? "Copied" : "Copy"}</span>
          </button>
        </div>
        <button
          type="button"
          className="handoff__body"
          onClick={copy}
          title="Tap to copy"
        >
          {children}
        </button>
      </div>
    </div>
  );
}

/* Inline SVG glyphs (no icon-font dependency; matches the app's naked-icon look). */
function LinkGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" width="15" height="15" aria-hidden="true">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  );
}
function CopyGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14" aria-hidden="true">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}
function CheckGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" width="14" height="14" aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
